import { inject, Injectable, signal } from '@angular/core';
import { RegisterUserResponse, SendRegisterUserResponse } from '../interfaces/register-user.response';
import { catchError, from, map, Observable, switchMap, throwError } from 'rxjs';
import { StatisticsService } from 'src/app/services/statistics.service';
import { RestService } from 'src/app/services/rest.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { registerUserFormAdapter } from '../adapters/register-user-form.adapter';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { environment } from 'src/environments/environment';
import { RegisterUserFormValues } from '../interfaces';
import { VisitorRegistrationBeforeModularFormField } from '../interfaces/register-user.response';
import { CustomRouteService } from '../../custom-route/services';
import { ToolsService } from 'src/app/services';
import { SimpleRegisterFlow } from './simple-register-flow.service';

@Injectable({
  providedIn: 'root'
})
export class RegisterUserService {
  // Injected services
  private _statisticsService = inject(StatisticsService);
  private _restService = inject(RestService);
  private _toolsService = inject(ToolsService);
  private _http = inject(HttpClient);
  private _router = inject(Router);
  private _sessionStorageService = inject(SessionStorageService);
  private _customRouteService = inject(CustomRouteService);
  private _simpleRegisterFlow = inject(SimpleRegisterFlow);

  // Signals state
  private _registerUser = signal<RegisterUserResponse | null>(null);
  public registerUser = this._registerUser.asReadonly();
  
  private _registerUserForm = signal<RegisterUserFormValues | null>(null);
  public registerUserForm = this._registerUserForm.asReadonly();
  
  private _numberOfDaysCustomRoute = signal<number>(0);
  public numberOfDaysCustomRoute = this._numberOfDaysCustomRoute.asReadonly();
  
  private _isLoadingRegisterUserData = signal<boolean>(false);
  public isLoadingRegisterUserData = this._isLoadingRegisterUserData.asReadonly();
  
  private _isLoadingFirstCustomRoute = signal<boolean>(false);
  public isLoadingFirstCustomRoute = this._isLoadingFirstCustomRoute.asReadonly();
  
  private _expandSearchRadiusMessage = signal<string | null>(null);
  public expandSearchRadiusMessage = this._expandSearchRadiusMessage.asReadonly();

  private _cityLocation = signal<Record<string, string> | undefined>(undefined);
  public cityLocation = this._cityLocation.asReadonly();
  
  private _device = this._restService.device;
  
  /**
   * Setters
   */
  setRegisterUserData(data: RegisterUserResponse): void {
    this._registerUser.set(data);
    this._sessionStorageService.setItem<RegisterUserResponse>('registerUser', data);
  }
  
  setRegisterUserFormData(data: RegisterUserFormValues): void {
    this._registerUserForm.set(data);
    this._sessionStorageService.setItem<RegisterUserFormValues>('registerUserForm', data);
  }
  
  setIsLoadingFirstCustomRoute(isLoading: boolean): void {
    this._isLoadingFirstCustomRoute.set(isLoading);
  }
  
  setNumberOfDaysCustomRoute(numberOfDays: number): void {
    this._numberOfDaysCustomRoute.set(numberOfDays);
    this._sessionStorageService.setItem<number>('numberOfDaysCustomRoute', numberOfDays);
  }
  
  setExpandSearchRadiusMessage(message: string | null): void {
    this._expandSearchRadiusMessage.set(message);
  }
  
  /**
   * Http requests
   */
  getDataRegisterUserServiceSignal() {
    if (this._registerUser()) return;
    
    this._isLoadingRegisterUserData.set(true);
    
    return this.getRegisterUserResponse()
      .pipe(
        catchError((error) => {
          this._isLoadingRegisterUserData.set(false);
          this._router.navigateByUrl("not-found");
          return throwError(() => new Error('HTTP request failed', { cause: error }));
        })
      )
      .subscribe((response: RegisterUserResponse) => {
        this._isLoadingRegisterUserData.set(false);
        this.setRegisterUserData(response);
        this._cityLocation.set(response.headerName);
    });
  }
  
  getRegisterUserResponse(): Observable<RegisterUserResponse> {
    return from(this._toolsService.waitUntilReady()).pipe(
      switchMap(([isApp]) => {
        const url = isApp
          ? this._statisticsService.token
            ? `${this._restService.apiUrlBase}/api/get-register-app/${this._restService.subDomain}/${this._statisticsService.token}`
            : `${this._restService.apiUrlBase}/api/get-register-app/${this._restService.subDomain}`
          : `${this._restService.apiUrlBase}/api/get-register-community-app/${this._restService.subDomain}`;

        // HRS-2990: los accesos directos del panel no traen canal, así que piden
        // explícitamente el registro elegido; sin esto llegarían los campos del
        // registro de la app. El id viaja en la ruta en el registro simple
        // (tech-register-by-id/:id) y en `?register=` en el registro completo.
        const registerId = this._simpleRegisterFlow.registerId()
          ?? this._toolsService.informerRegisterId();

        // HRS-2990: el modo viaja para que el CMS sepa que esto no es el planner.
        // Allí los tipos de viajero y las categorías se filtran por contenido
        // planificable, y un destino sin POIs ni eventos los recibía vacíos: el
        // técnico se quedaba sin poder registrarlos, que es el dato estadístico
        // que se le pide. En el informador se sirven los del CMS sin filtrar.
        const params = [
          registerId ? `register=${encodeURIComponent(registerId)}` : null,
          this._toolsService.informerMode() ? `informer=${encodeURIComponent(this._toolsService.informerMode()!)}` : null,
        ].filter(Boolean);
        const requestUrl = params.length ? `${url}?${params.join('&')}` : url;

        return this._http.get<RegisterUserResponse>(requestUrl);
      }),
      map((response: RegisterUserResponse) => {
        if(!environment.production) {
          console.log("getRegisterUserAppResponse:", response);
        }
        return response;
      }),
      catchError((error) => {
        throw new Error(`Error fetching register user data: ${error.message}`);
      })
    )
  }
  
  // Methods
  /**
   * Get city name from latitude and longitude using Google Maps Geocoding API.
   * @param lat Latitude of the location.
   * @param lng Longitude of the location.
   * @returns Promise that resolves to the city name or null if not found.
   */
  private async _getCityFromCoordinates(lat: number, lng: number): Promise<string | null> {
    if(!environment.production) return Promise.resolve(null);
    
    // google api verfication
    if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
      throw new Error('Google Maps API not available');
    }

    const geocoder = new google.maps.Geocoder();
    const latlng = { lat: Number(lat), lng: Number(lng) };
    
    return new Promise<string | null>((resolve, reject) => {
      geocoder.geocode({ location: latlng }, (results, status) => {
        if(!environment.production) {
          console.log('=== GEOCODING RESPONSE ===');
          console.log('Status:', status);
          console.log('Results:', results);
        }

        if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
          const addressComponents = results[0].address_components;
          
          const cityComponent = addressComponents.find(component => 
            component.types.includes('locality') ||
            component.types.includes('administrative_area_level_2') ||
            component.types.includes('administrative_area_level_1')
          );
          
          const cityName = cityComponent?.long_name || null;
          resolve(cityName);
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  }
  
  sendRegisterUserData(data: RegisterUserFormValues, modularFieldDefinitions: VisitorRegistrationBeforeModularFormField[] = []): Observable<SendRegisterUserResponse> {
    this._isLoadingFirstCustomRoute.set(true);

    const url = `${environment.apiAssistantUrlBase}/api/get-route-planning`;
    
    const device = this._device();
    if (!device) return throwError(() => new Error('Device not found'));
    const body = registerUserFormAdapter(device, data, modularFieldDefinitions);

    return this._http.post<SendRegisterUserResponse>(url, body).pipe(
      map((response: SendRegisterUserResponse) => {
        if(!environment.production) {
          console.log("sendRegisterUserData response:", response);
        }
        
        this._customRouteService.setToken(response.token);
        this._isLoadingFirstCustomRoute.set(true);
        return response;
      }),
      catchError((error) => {
        this._isLoadingFirstCustomRoute.set(false);
        console.error(`Error sending register user data: ${error.message}`);
        return throwError(() => new Error('HTTP request failed', { cause: error }));
      })
    );
  }
}
